import { type FC } from "react";
import {
  Dialog,
  Box,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  usePlatformMenuItemQuery,
  useUpdatePlatformMenuItemMutation,
} from "@/partner/entities/platformMenuItem";
import { MenuItemForm } from "./MenuItemForm";
import type { PlatformMenuItemFormValues } from "../model";

interface EditMenuItemDialogProps {
  open: boolean;
  menuItemId: string;
  onClose: () => void;
}

export const EditMenuItemDialog: FC<EditMenuItemDialogProps> = ({
  open,
  menuItemId,
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { data, loading } = usePlatformMenuItemQuery(menuItemId);

  const [updatePlatformMenuItem, { loading: updating }] =
    useUpdatePlatformMenuItemMutation({
      onCompleted: () => {
        enqueueSnackbar(t("platformMenu.snackbar.updated"), {
          variant: "success",
        });
        onClose();
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["PlatformMenuItemsList"],
    });

  if (loading || !data) {
    return (
      <Dialog
        fullScreen
        fullWidth
        maxWidth="md"
        scroll="body"
        open={open}
        onClose={onClose}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  const handleSubmit = async (values: PlatformMenuItemFormValues) => {
    await updatePlatformMenuItem({
      variables: {
        id: menuItemId,
        input: values,
      },
    });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      scroll="body"
      open={open}
      onClose={onClose}
    >
      <MenuItemForm
        title={t("platformMenu.edit.title")}
        loading={updating}
        initialValues={data.platformMenuItem}
        onSubmit={handleSubmit}
        onClose={onClose}
      />
    </Dialog>
  );
};
